import { onSnapshot as _onSnapshot, queryEqual, refEqual } from 'firebase/firestore'
import type {
  DocumentReference,
  Query,
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
} from 'firebase/firestore'
import { markFirstSnapshot } from '../perf'
import { logFile } from '../logger'
import { meterReads } from './readMeter'
import { listenTimer } from './latency'
import { tagFirestoreError } from '../firestoreErrorContext'
import {
  logGrace,
  logPermDenied,
  logReuse,
  logRevive,
  logSnapshot,
  logSubscribe,
  logUnsub,
  refPath,
} from './log'

type AnyRef = DocumentReference<DocumentData> | Query<DocumentData>
type AnySnap = DocumentSnapshot<DocumentData> | QuerySnapshot<DocumentData>

// Ref-counted subscription cache with grace period.
// When the last listener for a query unsubscribes, the underlying Firestore
// subscription stays open for GRACE_PERIOD_MS so a remount within that window
// reuses the live stream and gets the cached last snapshot immediately.
const GRACE_PERIOD_MS = 30_000

type Listener = (snap: AnySnap) => void
type ErrorListener = (err: FirestoreError) => void

type CacheEntry = {
  kind: 'doc' | 'query'
  ref: AnyRef
  path: string
  unsubscribe: Unsubscribe
  listeners: Set<Listener>
  errorListeners: Map<Listener, ErrorListener>
  lastSnap: AnySnap | null
  teardownTimer: ReturnType<typeof setTimeout> | null
  /** Whether a server snapshot has been metered yet — see readMeter. */
  billedOnce: boolean
}

const cache: CacheEntry[] = []

// Every snapshot arrives in its own task, so React committed once per snapshot —
// ~90 commits of the whole event page before it settled. Deliveries that land
// within one frame of each other run in a single task, which React batches
// into one commit. Bookkeeping (lastSnap, metering) stays immediate.
const DELIVERY_WINDOW_MS = 16
let pendingDeliveries: (() => void)[] = []
let deliveryTimer: ReturnType<typeof setTimeout> | null = null

function deliverSoon(fn: () => void) {
  pendingDeliveries.push(fn)
  deliveryTimer ??= setTimeout(() => {
    deliveryTimer = null
    const batch = pendingDeliveries
    pendingDeliveries = []
    batch.forEach(f => f())
  }, DELIVERY_WINDOW_MS)
}

function findEntry(ref: AnyRef): CacheEntry | undefined {
  const isDoc = (ref as { type?: string })?.type === 'document'
  return cache.find(e =>
    isDoc
      ? e.kind === 'doc' && refEqual(e.ref as DocumentReference, ref as DocumentReference)
      : e.kind === 'query' && queryEqual(e.ref as Query, ref as Query),
  )
}

export function getCachedSnapshot<T extends DocumentData>(
  ref: DocumentReference<T>,
): DocumentSnapshot<T> | null
export function getCachedSnapshot<T extends DocumentData>(
  ref: Query<T>,
): QuerySnapshot<T> | null
export function getCachedSnapshot(ref: AnyRef): AnySnap | null {
  return findEntry(ref)?.lastSnap ?? null
}

export function onSnapshot<T extends DocumentData>(
  ref: Query<T>,
  onNext: (snap: QuerySnapshot<T>) => void,
  onError?: (err: FirestoreError) => void,
): Unsubscribe
export function onSnapshot<T extends DocumentData>(
  ref: DocumentReference<T>,
  onNext: (snap: DocumentSnapshot<T>) => void,
  onError?: (err: FirestoreError) => void,
): Unsubscribe
export function onSnapshot(
  ref: AnyRef,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- impl signature must accept any overload
  onNext: (snap: any) => void,
  onError?: (err: FirestoreError) => void,
): Unsubscribe {
  const path = refPath(ref)
  let entry = findEntry(ref)

  if (entry) {
    if (entry.teardownTimer) {
      clearTimeout(entry.teardownTimer)
      entry.teardownTimer = null
      logRevive(path)
      logFile('fs', 'revive', { path })
    } else {
      logReuse(`${path}  (${entry.listeners.size + 1} subs)`)
      logFile('fs', 'reuse', { path, subs: entry.listeners.size + 1 })
    }
    entry.listeners.add(onNext)
    if (onError) entry.errorListeners.set(onNext, onError)
    if (entry.lastSnap) {
      const snap = entry.lastSnap
      const e = entry
      queueMicrotask(() => {
        if (e.listeners.has(onNext)) onNext(snap)
      })
    }
  } else {
    logSubscribe(path)
    logFile('fs', 'subscribe', { path })
    const newEntry: CacheEntry = {
      kind: (ref as { type?: string })?.type === 'document' ? 'doc' : 'query',
      ref,
      path,
      unsubscribe: null as unknown as Unsubscribe,
      listeners: new Set<Listener>([onNext]),
      errorListeners: new Map<Listener, ErrorListener>(),
      lastSnap: null,
      teardownTimer: null,
      billedOnce: false,
    }
    if (onError) newEntry.errorListeners.set(onNext, onError)
    cache.push(newEntry)
    const timeSnapshot = listenTimer(path)
    // Queries listen with metadata changes so a cache-first result that the
    // server then confirms unchanged still reaches listeners as `fromCache:
    // false` — Firestore otherwise drops that flip, and callers holding a
    // freeze until the server answers would wait forever. Pending-write flips
    // with no document change are filtered out to keep the default cadence.
    newEntry.unsubscribe = _onSnapshot(
      ref as Query<DocumentData>,
      { includeMetadataChanges: newEntry.kind === 'query' },
      (snap: AnySnap) => {
        const prev = newEntry.lastSnap
        if (
          newEntry.kind === 'query' &&
          prev &&
          prev.metadata.fromCache === snap.metadata.fromCache &&
          (snap as QuerySnapshot<DocumentData>).docChanges().length === 0
        ) return
        markFirstSnapshot()
        const n: number = 'size' in snap ? snap.size : 1
        const fromCache = snap.metadata.fromCache
        timeSnapshot(fromCache, n)
        logSnapshot({ path, n, subs: newEntry.listeners.size, fromCache })
        logFile('fs', 'snapshot', { path, n, fromCache })
        if (!fromCache) {
          // First server snapshot of a query: the full result set (min 1).
          // After that, only the changed docs are billed.
          const billed = newEntry.kind === 'doc' ? 1 : newEntry.billedOnce ? (snap as QuerySnapshot<DocumentData>).docChanges().length : Math.max(1, n)
          newEntry.billedOnce = true
          meterReads(path, billed)
        }
        newEntry.lastSnap = snap
        deliverSoon(() => newEntry.listeners.forEach(l => l(snap)))
      },
      (error: FirestoreError) => {
        tagFirestoreError(error, 'onSnapshot', path)
        // Forward to subscribed error listeners first (so UI can react before cache state is torn down).
        newEntry.errorListeners.forEach(l => l(error))
        if (error?.code === 'permission-denied') {
          // Auth state changed (e.g. logout) while grace-period subscription was live — clean up silently.
          logPermDenied(`${path}  (cleaning up)`)
          logFile('fs', 'permission-denied', { path })
          if (newEntry.teardownTimer) {
            clearTimeout(newEntry.teardownTimer)
            newEntry.teardownTimer = null
          }
          newEntry.listeners.clear()
          newEntry.errorListeners.clear()
          newEntry.unsubscribe()
          const idx = cache.indexOf(newEntry)
          if (idx >= 0) cache.splice(idx, 1)
        } else {
          logFile('fs', 'error', { path, code: error.code, message: error.message })
        }
      },
    )
    entry = newEntry
  }

  const e = entry
  let unsubscribed = false
  return () => {
    if (unsubscribed) return
    unsubscribed = true
    e.listeners.delete(onNext)
    e.errorListeners.delete(onNext)
    if (e.listeners.size === 0) {
      logGrace(path)
      logFile('fs', 'grace', { path })
      e.teardownTimer = setTimeout(() => {
        if (e.listeners.size === 0) {
          logUnsub(path)
          logFile('fs', 'unsubscribe', { path })
          e.unsubscribe()
          const idx = cache.indexOf(e)
          if (idx >= 0) cache.splice(idx, 1)
        }
      }, GRACE_PERIOD_MS)
    }
  }
}
